import { mkdir, readFile, readdir, rm, writeFile } from 'node:fs/promises';
import path from 'node:path';

/**
 * Mode skill names — used for .agents/skills/ (Codex + ZCode), .cursor/commands/,
 * .cursor/commands/, .github/prompts/
 */
export const MODE_NAMES = [
  'mamba',
  'man',
  'manteam',
  'manps',
  'mansolo',
] as const;
const LEGACY_MODE_NAMES = ['man8'] as const;
export type ModeName = (typeof MODE_NAMES)[number];

export const CODEX_SKILL_MANAGED_MARKER =
  '<!-- Managed by mancode:codex-skill. Do not edit this file manually. -->';

export const ZCODE_SKILL_MANAGED_MARKER =
  '<!-- Managed by mancode:zcode-skill. Do not edit this file manually. -->';

export const MODE_FILE_MANAGED_MARKER =
  '<!-- Managed by mancode:mode-file. Do not edit this file manually. -->';

/**
 * Markers that identify a skill file as mancode-managed. Codex and ZCode share
 * the .agents/skills/ directory (both follow the agentskills.io standard), so
 * either marker means the file is safe to overwrite on reinstall.
 */
export const MANCODE_AGENT_SKILL_MARKERS = [
  CODEX_SKILL_MANAGED_MARKER,
  ZCODE_SKILL_MANAGED_MARKER,
] as const;

/**
 * Generate SKILL.md body content for a mode.
 *
 * Shared across Codex + ZCode (.agents/skills/), Cursor (.cursor/commands/),
 * Cursor (.cursor/commands/), and Copilot (.github/prompts/).
 * The content is platform-agnostic —
 * it assumes no subagents, no hooks, and instructs the AI to simulate
 * the coaching staff in sequence within a single conversation.
 *
 * @param commandPrefix Platform-specific invocation prefix:
 *   - Codex uses `$` (e.g. `$mamba`)
 *   - Cursor uses `/` (e.g. `/mamba`)
 *   - Copilot uses `''` (prompt-file selection, no prefix)
 */
export function renderModeSkill(
  mode: ModeName,
  commandPrefix: '$' | '/' | '' = '$',
): string {
  const meta = MODE_META[mode];
  // mansolo maps to "solo" in state.json — "mansolo" is not a valid currentMode value.
  const stateMode = mode === 'mansolo' ? 'solo' : mode;
  const modesList = MODE_NAMES.map((m) => `${commandPrefix}${m}`).join(', ');
  return [
    meta.intro,
    '',
    '## Mode Persistence',
    '',
    'Continue following these instructions for ALL subsequent tasks in this',
    `conversation until the user switches modes (${modesList}).`,
    '',
    'When entering this mode, update `.mancode/state.json` and set',
    `"currentMode" to "${stateMode}" so the mode persists across session restarts.`,
    'If unsure whether a mode is active, read `.mancode/state.json` to verify.',
    '',
    '## Practice',
    '',
    'Before writing new code, check the YAGNI ladder:',
    '',
    '1. Reuse existing project code.',
    '2. Use the standard library.',
    '3. Use platform-native behavior.',
    '4. Use already installed dependencies.',
    '5. Prefer a one-line fix when it is enough.',
    '6. Only then write the smallest new implementation.',
    '',
    'For every task, consider: why this change, what already exists, and what',
    'is the smallest useful diff?',
    '',
    'Read `.mancode/project-profile.json` before choosing tools or validation.',
    'Only for a UI task in a profile with detected UI assets, read',
    '`.mancode/aesthetics/style-tokens.json` before inventing new styles.',
    '',
    meta.workflow,
  ].join('\n');
}

/**
 * Write mode skill files for Codex in the ChatGPT desktop app and Codex CLI
 * (.agents/skills/<name>/SKILL.md).
 */
export async function installCodexSkills(
  projectRoot: string,
  minimal: boolean,
): Promise<void> {
  if (minimal) {
    await removeCodexSkills(projectRoot);
    return;
  }
  const skillsDir = path.join(projectRoot, '.agents', 'skills');
  await removeLegacyManagedSkills(skillsDir, MANCODE_AGENT_SKILL_MARKERS);
  for (const mode of MODE_NAMES) {
    const modeDir = path.join(skillsDir, mode);
    await mkdir(modeDir, { recursive: true });
    const meta = MODE_META[mode];
    const content = [
      '---',
      `name: ${mode}`,
      `description: ${JSON.stringify(meta.description)}`,
      '---',
      '',
      CODEX_SKILL_MANAGED_MARKER,
      '',
      renderModeSkill(mode, '$'),
      '',
    ].join('\n');
    await writeManagedSkill(
      path.join(modeDir, 'SKILL.md'),
      content,
      CODEX_SKILL_MANAGED_MARKER,
      'Codex',
      MANCODE_AGENT_SKILL_MARKERS,
    );
  }
}

/**
 * Write mode skill files for ZCode (.agents/skills/<name>/SKILL.md).
 * ZCode shares the .agents/skills/ directory with Codex (both follow the
 * agentskills.io standard). Skill content is identical; only the managed
 * marker differs.
 */
export async function installZcodeSkills(
  projectRoot: string,
  minimal: boolean,
): Promise<void> {
  if (minimal) {
    await removeZcodeSkills(projectRoot);
    return;
  }
  const skillsDir = path.join(projectRoot, '.agents', 'skills');
  await removeLegacyManagedSkills(skillsDir, MANCODE_AGENT_SKILL_MARKERS);
  for (const mode of MODE_NAMES) {
    const modeDir = path.join(skillsDir, mode);
    await mkdir(modeDir, { recursive: true });
    const meta = MODE_META[mode];
    const content = [
      '---',
      `name: ${mode}`,
      `description: ${JSON.stringify(meta.description)}`,
      '---',
      '',
      ZCODE_SKILL_MANAGED_MARKER,
      '',
      renderModeSkill(mode, '$'),
      '',
    ].join('\n');
    await writeManagedSkill(
      path.join(modeDir, 'SKILL.md'),
      content,
      ZCODE_SKILL_MANAGED_MARKER,
      'ZCode',
      MANCODE_AGENT_SKILL_MARKERS,
    );
  }
}

/**
 * Write command files for Cursor (.cursor/commands/<name>.md).
 */
export async function installCursorCommands(
  projectRoot: string,
  minimal: boolean,
): Promise<void> {
  if (minimal) {
    await removeCursorCommands(projectRoot);
    return;
  }
  const commandsDir = path.join(projectRoot, '.cursor', 'commands');
  await mkdir(commandsDir, { recursive: true });
  await removeLegacyGeneratedModeFile(path.join(commandsDir, 'man8.md'));
  for (const mode of MODE_NAMES) {
    const meta = MODE_META[mode];
    const content = [
      '---',
      `description: ${JSON.stringify(meta.description)}`,
      '---',
      '',
      MODE_FILE_MANAGED_MARKER,
      '',
      renderModeSkill(mode, '/'),
      '',
    ].join('\n');
    await writeManagedModeFile(
      path.join(commandsDir, `${mode}.md`),
      content,
      mode,
      'Cursor',
    );
  }
}

/**
 * Write prompt files for Copilot (.github/prompts/<name>.prompt.md).
 */
export async function installCopilotPrompts(
  projectRoot: string,
  minimal: boolean,
): Promise<void> {
  if (minimal) {
    await removeCopilotPrompts(projectRoot);
    return;
  }
  const promptsDir = path.join(projectRoot, '.github', 'prompts');
  await mkdir(promptsDir, { recursive: true });
  await removeLegacyGeneratedModeFile(path.join(promptsDir, 'man8.prompt.md'));
  for (const mode of MODE_NAMES) {
    const meta = MODE_META[mode];
    const content = [
      '---',
      "agent: 'agent'",
      `description: ${JSON.stringify(meta.description)}`,
      '---',
      '',
      MODE_FILE_MANAGED_MARKER,
      '',
      renderModeSkill(mode, ''),
      '',
    ].join('\n');
    await writeManagedModeFile(
      path.join(promptsDir, `${mode}.prompt.md`),
      content,
      mode,
      'GitHub Copilot',
    );
  }
}

export async function removeCodexSkills(projectRoot: string): Promise<void> {
  // Current path — Codex scans .agents/skills/ in the ChatGPT desktop app,
  // CLI, and IDE extension (learn.chatgpt.com/docs/build-skills).
  // Codex and ZCode share this directory. Preserve it while a non-minimal
  // ZCode adapter still needs the same generated skills; otherwise either
  // platform marker is safe to remove because the shared content is identical.
  if (!(await platformNeedsSharedSkills(projectRoot, 'zcode'))) {
    await cleanManagedSkills(
      path.join(projectRoot, '.agents', 'skills'),
      MANCODE_AGENT_SKILL_MARKERS,
    );
  }
  // Legacy path — pre-fix versions (<= 0.2.0) wrote to .codex/skills/, which
  // Codex never scanned. Clean managed files so upgrading users don't leave
  // stale zombie skills behind on uninstall.
  await cleanManagedSkills(path.join(projectRoot, '.codex', 'skills'), [
    CODEX_SKILL_MANAGED_MARKER,
  ]);
}

async function cleanManagedSkills(
  skillsDir: string,
  markers: readonly string[],
): Promise<void> {
  for (const mode of [...MODE_NAMES, ...LEGACY_MODE_NAMES]) {
    const modeDir = path.join(skillsDir, mode);
    const skillPath = path.join(modeDir, 'SKILL.md');
    if (await isManagedByAnyMarker(skillPath, markers)) {
      await rm(skillPath, { force: true });
      await removeIfEmpty(modeDir);
    }
  }
  await removeIfEmpty(skillsDir);
}

export async function removeZcodeSkills(projectRoot: string): Promise<void> {
  // Current path — ZCode shares .agents/skills/ with Codex (agentskills.io).
  if (!(await platformNeedsSharedSkills(projectRoot, 'codex'))) {
    await cleanManagedSkills(
      path.join(projectRoot, '.agents', 'skills'),
      MANCODE_AGENT_SKILL_MARKERS,
    );
  }
  // Legacy path — pre-fix versions wrote to .zcode/skills/.
  await cleanManagedSkills(path.join(projectRoot, '.zcode', 'skills'), [
    ZCODE_SKILL_MANAGED_MARKER,
  ]);
}

export async function removeCursorCommands(projectRoot: string): Promise<void> {
  for (const mode of [...MODE_NAMES, ...LEGACY_MODE_NAMES]) {
    const filePath = path.join(
      projectRoot,
      '.cursor',
      'commands',
      `${mode}.md`,
    );
    if (mode === 'man8') await removeLegacyGeneratedModeFile(filePath);
    else await removeManagedModeFile(filePath, mode);
  }
}

export async function removeCopilotPrompts(projectRoot: string): Promise<void> {
  for (const mode of [...MODE_NAMES, ...LEGACY_MODE_NAMES]) {
    const filePath = path.join(
      projectRoot,
      '.github',
      'prompts',
      `${mode}.prompt.md`,
    );
    if (mode === 'man8') await removeLegacyGeneratedModeFile(filePath);
    else await removeManagedModeFile(filePath, mode);
  }
}

async function writeManagedModeFile(
  filePath: string,
  content: string,
  mode: ModeName,
  platformLabel: string,
): Promise<void> {
  const existing = await readTextIfExists(filePath);
  if (existing !== null && !isGeneratedModeFile(existing, mode)) {
    throw new Error(
      `refusing to overwrite user-authored ${platformLabel} mode file: ${filePath}`,
    );
  }
  await writeFile(filePath, content, 'utf-8');
}

async function removeManagedModeFile(
  filePath: string,
  mode: ModeName,
): Promise<void> {
  const content = await readTextIfExists(filePath);
  if (content && isGeneratedModeFile(content, mode)) {
    await rm(filePath, { force: true });
  }
}

async function removeLegacyGeneratedModeFile(filePath: string): Promise<void> {
  const content = await readTextIfExists(filePath);
  if (
    content?.includes('# mancode man8 — Investigate and Plan') &&
    content.includes('## Mode Persistence')
  ) {
    await rm(filePath, { force: true });
  }
}

function isGeneratedModeFile(content: string, mode: ModeName): boolean {
  return (
    content.includes(MODE_FILE_MANAGED_MARKER) ||
    (content.includes(`# mancode ${mode}`) &&
      content.includes('## Mode Persistence'))
  );
}

async function removeLegacyManagedSkills(
  skillsDir: string,
  markers: readonly string[],
): Promise<void> {
  for (const mode of LEGACY_MODE_NAMES) {
    const modeDir = path.join(skillsDir, mode);
    const skillPath = path.join(modeDir, 'SKILL.md');
    const content = await readTextIfExists(skillPath);
    if (content && markers.some((marker) => content.includes(marker))) {
      await rm(skillPath, { force: true });
      await removeIfEmpty(modeDir);
    }
  }
}

async function removeIfEmpty(dir: string): Promise<void> {
  try {
    const entries = await readdir(dir);
    if (entries.length === 0) {
      await rm(dir, { recursive: true, force: true });
    }
  } catch {
    // Directory does not exist or cannot be read — nothing to clean.
  }
}

async function writeManagedSkill(
  skillPath: string,
  content: string,
  marker: string,
  platformLabel: string,
  recognizedMarkers: readonly string[] = [marker],
): Promise<void> {
  const existing = await readTextIfExists(skillPath);
  if (
    existing !== null &&
    !recognizedMarkers.some((m) => existing.includes(m))
  ) {
    throw new Error(
      `refusing to overwrite user-authored ${platformLabel} skill: ${skillPath}`,
    );
  }
  await writeFile(skillPath, content, 'utf-8');
}

async function isManagedByAnyMarker(
  skillPath: string,
  markers: readonly string[],
): Promise<boolean> {
  const content = await readTextIfExists(skillPath);
  return content !== null && markers.some((marker) => content.includes(marker));
}

async function platformNeedsSharedSkills(
  projectRoot: string,
  platform: 'codex' | 'zcode',
): Promise<boolean> {
  try {
    const raw = await readFile(
      path.join(projectRoot, '.mancode', 'config.json'),
      'utf-8',
    );
    const config = JSON.parse(raw) as {
      platforms?: unknown;
      platformOptions?: unknown;
    };
    if (
      !Array.isArray(config.platforms) ||
      !config.platforms.includes(platform)
    ) {
      return false;
    }
    if (!isRecord(config.platformOptions)) return true;
    const options = config.platformOptions[platform];
    return !isRecord(options) || options.minimal !== true;
  } catch {
    return false;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

async function readTextIfExists(filePath: string): Promise<string | null> {
  try {
    return await readFile(filePath, 'utf-8');
  } catch {
    return null;
  }
}

interface ModeMeta {
  description: string;
  intro: string;
  workflow: string;
}

const MODE_META: Record<ModeName, ModeMeta> = {
  mamba: {
    description:
      'Diagnose bugs and validate real user flows with targeted regression checks.',
    intro: '# mancode mamba — Diagnosis and Real Validation',
    workflow: [
      '## Five-step workflow (simulate roles in one conversation)',
      '',
      'Use `mancode workflow create mamba "<task>" --json`; when called from',
      'a /man or /manteam Step 6 workflow, add `--parent-task <taskId>`. Persist the returned',
      'taskId in `.mancode/state.json` as currentTask; set currentMode and',
      'currentWorkflowMode to mamba. Never edit metadata.json directly; use',
      '`mancode workflow update` for every step and final state.',
      'If state points to an unrelated active workflow, stop and ask the user to resume it or abandon it through mansolo; never overwrite its pointer.',
      '',
      '1. Define the symptom, environment, expected/actual result, protected data, and success criteria; then run `mancode workflow update <taskId> --step 2`.',
      '2. Prepare the safest available environment. Full capability may use a detected Web UI and Playwright; command-only capability produces a manual browser checklist; restricted capability records manual_test_required. When ready, update to Step 3.',
      '   Starting local services and read-only probes is allowed. Dependency downloads, credentials, seed/reset, external login, messages, payments, deletion, and production writes require explicit approval.',
      '   If environment, permission, or data is missing, set blocked with `--blocking-reason` and keep state resumable instead of advancing.',
      '3. Reproduce the shortest path and write `diagnosis.md` with evidence, root cause or candidates, impact, and confidence; then update to Step 4.',
      '4. Apply only the requested minimum fix, or make no code change when the user requested validation only. New architecture or cross-module scope returns to /man. Then update to Step 5.',
      '5. Re-run the path and affected positive, negative, and permission boundaries; write `mamba-report.md` with environment, evidence, artifacts, regression scope, and risks.',
      '',
      'Finish through the CLI with exactly one result: completed+fixed, completed+verified, completed+no_repro, completed+manual_test_required, or blocked+blockingReason. Never claim real testing in restricted mode. Child blocked/manual_test_required propagation is handled by the CLI. After a child finishes with fixed/verified/no_repro, inspect its parent; if that parent is still blocked because of this child, run `mancode workflow update <parentTaskId> --status in_progress` before restoring parent state at Step 6. Never auto-resume manual_test_required.',
    ].join('\n'),
  },
  man: {
    description:
      'Full high-risk workflow with plan, implementation, verification, and review. Use when the user asks for man or a high-risk production change.',
    intro: '# mancode man — Full Review Workflow',
    workflow: [
      '## Progressive nine-step workflow (simulate roles in one conversation)',
      '',
      'Create the durable workflow with `mancode workflow create man "<task>" --json`,',
      'persist its taskId in `.mancode/state.json` as currentTask, set currentMode',
      'and currentWorkflowMode to man, and use `mancode workflow update`',
      'for every step/status/plan revision. Never edit metadata.json directly.',
      'After each completed phase, advance through the CLI to the next numbered step;',
      'never advance state merely because a report was requested or an agent was called.',
      '',
      'Step 1 — Scout: inspect the profile, existing implementation, dependencies, risks, and unknowns; write `scout-report.md`.',
      'Step 2 — Clarify: ask at most two rounds; write answers, conservative defaults, and unresolved risks to `requirements.md`.',
      'Step 3 — Plan: write `plan.md` with scope, reuse, exclusions, risks, completion criteria, and real validation. First plan is v1; revisions use `--plan-version <current+1>` and Active Plans is updated by the CLI.',
      'Step 4 — Plan gate: the user chooses plan-only (`status planned`), continue to execution, or revise the plan. Do not implement before this choice.',
      'Step 5 — Implement the confirmed minimum change.',
      'Step 6 — Run detected build/lint/typecheck/test and smoke checks. A real or complex diagnosis creates a linked mamba child while the parent remains at Step 6. After a resolved child, restore a parent blocked by that child with `workflow update --status in_progress` before Step 7; never auto-resume manual_test_required.',
      'Step 7 — Film #1 code-quality review; fix all required findings and explain suggested-item tradeoffs.',
      'Step 8 — Film #2 security/boundary review; keep it independent from Film #1. The two review passes are skipped only on explicit user instruction and the residual risk is recorded.',
      'Step 9 — Fix, re-verify, write `summary.md`, archive decisions, and set completed only when verification passes and required findings are zero; otherwise set blocked+blockingReason.',
      '',
      'After planned/completed/abandoned, return state to solo. On blocked, preserve enough state to resume. A CLI rejection is a hard stop, never a reason to bypass metadata validation.',
    ].join('\n'),
  },
  manteam: {
    description:
      'Team-aware workflow with handoff-friendly summaries. Use when the user asks for manteam or when the task affects shared team context.',
    intro: '# mancode manteam — Team Workflow',
    workflow: [
      '## Team workflow',
      '',
      'Create with `mancode workflow create manteam "<task>" --json`; never edit',
      'metadata.json directly. Persist its taskId as currentTask and set currentMode',
      'and currentWorkflowMode to manteam. First collect branch, dirty-worktree, contributor,',
      'shared-module, and handoff risks in `team-context.md`.',
      '',
      'Then execute every /man Step 1 through Step 9, including clarification,',
      'plan gate, validation, two review passes, conditional completion, planVersion,',
      'Active Plans, and linked mamba child behavior. Add compatibility, rollback,',
      'ownership, conflict, and handoff notes at each relevant step.',
      'When a resolved mamba child had blocked the parent, restore the parent through',
      '`workflow update --status in_progress`; never auto-resume manual_test_required.',
      '',
      'Read `.mancode/memory/prd.md`, `spec.md`, and `decisions.md`; append durable',
      'decisions only after confirmed implementation. Write `handoff.md`. Never',
      'overwrite teammate changes or commit/merge/push without explicit approval.',
    ].join('\n'),
  },
  manps: {
    description:
      'Run project health and cleanup scans before remediation. Use when the user asks for manps or project health cleanup.',
    intro: '# mancode manps — Project Health Scan',
    workflow: [
      '## Workflow',
      '',
      '1. Run `mancode manps [area]` to scan for tech debt, unused dependencies,',
      '   security issues, dead code, and config drift.',
      '2. Supported areas: all, deps, security, dead-code, config.',
      '3. Treat scan output as triage data — do not remediate without clear',
      '   user approval for each item.',
      '4. Present findings as a prioritized list with severity and recommendation.',
    ].join('\n'),
  },
  mansolo: {
    description:
      'Exit any active mancode mode and return to default solo behavior.',
    intro: '# mancode mansolo — Return to Solo',
    workflow: [
      '## Solo Behavior',
      '',
      'You are now in solo (default) mode.',
      '',
      'If state points to an active workflow, show it with `mancode workflow show',
      '<taskId> --json`, ask for confirmation, abandon active children before their',
      'parent through `mancode workflow update ... --status abandoned`, and only',
      'then update `.mancode/state.json` to solo. Never edit metadata.json directly.',
      '',
      '- Keep the diff narrow.',
      '- Reuse existing functions, components, styles, and dependencies.',
      '- Read project-profile first; for detected UI assets and UI work, read `.mancode/aesthetics/style-tokens.json`.',
      '- Verify with the narrowest meaningful test, lint, build, or smoke check.',
    ].join('\n'),
  },
};
